import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import skillsData from '../../data/skills.json';

const SkillsAdmin = () => {
  const [skillGroups, setSkillGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [newSkillItem, setNewSkillItem] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Load skills from the JSON file
    setSkillGroups(skillsData);
  }, []);

  const handleSaveSkills = () => {
    // In a real implementation, this would save to the server
    setSuccessMessage('Skills saved successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    
    console.log('Skills to save:', skillGroups);
    
    // You would typically use an API call here:
    // fetch('/api/skills', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(skillGroups)
    // })
  };

  const handleAddGroup = () => {
    if (!newGroupTitle.trim()) return;
    
    const newId = Math.max(0, ...skillGroups.map(g => g.id)) + 1;
    const newGroup = {
      id: newId,
      title: newGroupTitle,
      items: []
    };
    
    setSkillGroups([...skillGroups, newGroup]);
    setNewGroupTitle('');
    setIsAddingGroup(false);
  };

  const handleEditGroup = (group) => {
    setEditingGroup({ ...group });
  };

  const handleDeleteGroup = (id) => {
    if (window.confirm('Are you sure you want to delete this skill group?')) {
      setSkillGroups(skillGroups.filter(group => group.id !== id));
    }
  };

  const handleUpdateGroup = () => {
    setSkillGroups(skillGroups.map(g => g.id === editingGroup.id ? editingGroup : g));
    setEditingGroup(null);
  };

  const handleAddSkillItem = () => {
    if (!newSkillItem.trim()) return;
    
    setEditingGroup({
      ...editingGroup,
      items: [...editingGroup.items, newSkillItem]
    });
    
    setNewSkillItem('');
  };

  const handleRemoveSkillItem = (index) => {
    setEditingGroup({
      ...editingGroup,
      items: editingGroup.items.filter((_, i) => i !== index)
    });
  };

  return (
    <Container>
      <Header>
        <h2>Manage Skills</h2>
        <AddButton onClick={() => setIsAddingGroup(true)}>
          <FaPlus /> Add Skill Group
        </AddButton>
      </Header>

      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

      {isAddingGroup && (
        <FormContainer>
          <h3>Add New Skill Group</h3>
          <FormGroup>
            <label>Group Title</label>
            <input
              type="text"
              value={newGroupTitle}
              onChange={(e) => setNewGroupTitle(e.target.value)}
              placeholder="e.g. Frontend Development"
            />
          </FormGroup>
          <ButtonGroup>
            <SaveButton onClick={handleAddGroup}>Add Group</SaveButton>
            <CancelButton onClick={() => setIsAddingGroup(false)}>Cancel</CancelButton>
          </ButtonGroup>
        </FormContainer>
      )}

      {editingGroup && (
        <FormContainer>
          <h3>Edit Skill Group</h3>
          <FormGroup>
            <label>Group Title</label>
            <input
              type="text"
              value={editingGroup.title}
              onChange={(e) => setEditingGroup({...editingGroup, title: e.target.value})}
              placeholder="e.g. Frontend Development"
            />
          </FormGroup>

          <FormGroup>
            <label>Skills</label>
            <SkillItemsContainer>
              {editingGroup.items.map((item, index) => (
                <SkillItemRow key={index}>
                  <span>{item}</span>
                  <RemoveButton onClick={() => handleRemoveSkillItem(index)}>
                    <FaTrash />
                  </RemoveButton>
                </SkillItemRow>
              ))}
            </SkillItemsContainer>

            <SkillInputContainer>
              <input
                type="text"
                value={newSkillItem}
                onChange={(e) => setNewSkillItem(e.target.value)}
                placeholder="New skill"
              />
              <AddItemButton onClick={handleAddSkillItem}>
                <FaPlus />
              </AddItemButton>
            </SkillInputContainer>
          </FormGroup>

          <ButtonGroup>
            <SaveButton onClick={handleUpdateGroup}>Update Group</SaveButton>
            <CancelButton onClick={() => setEditingGroup(null)}>Cancel</CancelButton>
          </ButtonGroup>
        </FormContainer>
      )}

      <SkillGroupsList>
        {skillGroups.map(group => (
          <SkillGroupItem key={group.id}>
            <GroupInfo>
              <GroupTitle>{group.title}</GroupTitle>
              <SkillCount>{group.items.length} skills</SkillCount>
            </GroupInfo>
            <ItemsList>
              {group.items.map((item, index) => (
                <SkillItem key={index}>{item}</SkillItem>
              ))}
            </ItemsList>
            <GroupActions>
              <EditButton onClick={() => handleEditGroup(group)}>
                <FaEdit /> Edit
              </EditButton>
              <DeleteButton onClick={() => handleDeleteGroup(group.id)}>
                <FaTrash /> Delete
              </DeleteButton>
            </GroupActions>
          </SkillGroupItem>
        ))}
      </SkillGroupsList>

      <SaveAllButton onClick={handleSaveSkills}>Save All Changes</SaveAllButton>
    </Container>
  );
};

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    color: var(--text-primary);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(100, 255, 218, 0.1);
  color: var(--accent);
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
`;

const FormContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  
  input {
    width: 100%;
    padding: 10px;
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: var(--text-primary);
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: var(--accent);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: transparent;
  color: var(--text-primary);
  border: 1px solid var(--text-secondary);
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const SkillGroupsList = styled.div`
  margin-top: 30px;
`;

const SkillGroupItem = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
`;

const GroupInfo = styled.div`
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GroupTitle = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.2rem;
`;

const SkillCount = styled.span`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const ItemsList = styled.div`
  padding: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const SkillItem = styled.div`
  padding: 6px 12px;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
  border-radius: 20px;
  font-size: 0.9rem;
`;

const GroupActions = styled.div`
  padding: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: transparent;
  color: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: transparent;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 107, 107, 0.1);
  }
`;

const SaveAllButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px;
  margin-top: 20px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SkillItemsContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 10px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 5px 10px;
`;

const SkillItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
`;

const SkillInputContainer = styled.div`
  display: flex;
  gap: 10px;
  
  input {
    flex: 1;
  }
`;

const AddItemButton = styled.button`
  padding: 10px;
  background-color: var(--accent);
  color: var(--secondary);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

export default SkillsAdmin;